import { styled } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { checkDuplicatePageName } from '../../pages/aggregates/utils/pageUtils';
import { V2OptionsInput } from '../FormElements/V2OptionsInput';
import { PageContext } from './contexts/PageContext';
import { usePageSchemas } from './hooks/usePageSchemas';
import usePageRedirect from './hooks/usePageRedirect';
import { TextInputTG as TextInput } from 'views/components/formElements';
import { nameHelperTxt, useCustomPageStyles } from './commonStyles';
import { isEqual } from 'lodash';
import ModalFrame from './ModalFrame';
import MapperItem from 'components/FormDialog/MapperItem';
import { useConfirmDialog } from 'context/ConfirmContext';
import { CONFIRMATION, getPresentValue } from 'utils/Utils';
import { V2PageComponent, V2SelectComponent } from '@terragotech/page-renderer';

export type OptionsComponentDefWithName = (V2SelectComponent & {
      name: string;
      droppableId: string;
      index?: number;
    }) & V2PageComponent;

interface OptionsEditFormFormProps {
  onSubmit: (result: OptionsComponentDefWithName) => void;
  onClose: () => void;
  component: OptionsComponentDefWithName;
}

const TextInputTG = styled(TextInput)({
  '&.MuiFormControl-root': {
    marginBottom: 29,
  },
  '& .MuiInputBase-input': {
    fontSize: 15,
    fontWeight: 400,
  },
});

export const OptionsEditForm: React.FC<OptionsEditFormFormProps> = ({ onSubmit, onClose, component }) => {
  const [name, setName] = useState(component.name);
  const [label, setLabel] = useState<string>(component.label);
  const [conditionalOpen, setConditionalOpen] = useState(false);
  const [options, setOptions] = useState(component.options);
  const [displayOptions, setDisplayOptions] = useState(component.displayOptions);
  const classes = useCustomPageStyles();
  const pageSchemas = usePageSchemas();
  const { isForceLeaveConfirmed } = usePageRedirect();

  const { openConfirmation } = useConfirmDialog();

  const [existingNameError, setExistingNameError] = useState(false);
  const [emptyNameError, setEmptyNameError] = useState(false);
  const [emptyLabelError, setEmptyLabelError] = useState(false);
  const { pageDefinition } = useContext(PageContext);

  useEffect(() => {
    setExistingNameError(checkDuplicatePageName(name, component, pageDefinition.elements));
    setEmptyNameError(checkEmptyText(name));
  }, [name, component, pageDefinition.elements]);

  useEffect(() => {
    setEmptyLabelError(checkEmptyText(label));
  }, [label]);

  const checkEmptyText = (text: string) => {
    return text.trim() === '';
  };

  const handleClearDisplayMapper = async () => {
    const status = await openConfirmation(CONFIRMATION.commonClear);
    if (status === 'confirm') {
      setDisplayOptions(undefined);
    }
  };

  const getValue = (value: unknown, key: string) => getPresentValue(component, value, key);

  const getFormValues = () => ({
    type: component.type,
    name: name || '',
    label: label || '',
    ...getValue(options, 'options'),
    ...(displayOptions && { displayOptions }),
    droppableId: component.droppableId,
  });

  const isFormDirty = () => !isEqual(component, getFormValues());

  const handleClose = async () =>
    (!isFormDirty() || (isFormDirty() && (await isForceLeaveConfirmed({ handleSubmit })))) &&
    onClose();

  const handleSubmit = async () => {
    if (existingNameError || emptyNameError || emptyLabelError) {
      return;
    } else onSubmit(getFormValues() as OptionsComponentDefWithName);
  };

  return (
    <ModalFrame
      {...{ name: component.name, type: component.type, classes, handleClose, handleSubmit }}
    >
      <TextInputTG
        autoFocus
        id="Name"
        label="Name"
        error={existingNameError || emptyNameError}
        helperText={
          emptyNameError ? 'Name cannot be empty' : existingNameError ? nameHelperTxt : ''
        }
        value={name}
        onChange={(value) => setName(value || '')}
        style={styles.inputStyles}
        fullWidth
        labelRoot={classes.Root}
      />
      <TextInputTG
        id="Label"
        label="Label"
        error={emptyLabelError}
        helperText={emptyLabelError ? 'Label cannot be empty' : ''}
        value={label}
        onChange={(value) => setLabel(value || '')}
        style={styles.inputStyles}
        fullWidth
        labelRoot={classes.Root}
      />
      <MapperItem
        {...{
          onToggleMapper: setConditionalOpen,
          isActive: displayOptions !== undefined,
          clearMapper: handleClearDisplayMapper,
          openDataMap: conditionalOpen,
          dataMap: displayOptions,
          setDataMap: setDisplayOptions,
          localSchemaDefinition: pageSchemas,
          title: 'Display Options',
          mapScenario: 'DISPLAY_OPTIONS',
        }}
      />
      <V2OptionsInput
        options={options}
        setOptions={setOptions}
        localSchemas={pageSchemas}
      />
    </ModalFrame>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  inputStyles: {
    width: '100%',
  },
  mt2: {
    marginTop: 2,
  },
};
